import { CloseOutlined, FileAddOutlined } from '@ant-design/icons'
import {
  addCommasToNumber,
  GraviButton,
  GraviGrid,
  Horizontal,
  MaterialIcon,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import type { ColDef, GridApi, ICellRendererParams, IFilter, RowClassParams, RowStyle } from 'ag-grid-community'
import { Card } from 'antd'
import dayjs from 'dayjs'

import type { Invoice, InvoiceDetail, InvoiceGroup, InvoiceTransaction } from '../../../../../api/useFreight/types'
import { useUserSettings } from '../../../../../hooks/settings'
import { useMemo, type MutableRefObject, type Ref } from 'react'
import type { GridConfigState } from '@gravitate-js/excalibrr/dist/components/GraviGrid/hooks/useAGGridEvent'
import { AddFreightLineItemPopover } from '@/components/Freight/FreightLineItems/components/AddFreightLineItem'
import { formatters } from '@/utils/intl/dateTimeFormatters'

export function OrderLog({
  selectedInvoice,
  setSelectedOrder,
  orderLogGridRef,
  selectedOrder,
  isRebillMode,
  rebillUpdateActions,
  triggerDrawer,
  invoiceGroupDetails,
}: {
  selectedInvoice: Invoice
  setSelectedOrder: (order: InvoiceTransaction | null) => void
  orderLogGridRef: Ref<GridApi<InvoiceTransaction>>
  selectedOrder: InvoiceTransaction | null
  isRebillMode: boolean
  invoiceGroupDetails: InvoiceGroup
  rebillUpdateActions: {
    addRebillLineItem?: (detail: InvoiceDetail, orderId: string) => void
  }
  triggerDrawer: (orderId: string) => void
}) {
  const { updateSetting } = useUserSettings()
  const orderLogColumnDefs = useMemo(() => getOrderLogColumnDefs(selectedInvoice || null), [selectedInvoice])

  function handleRowDeselection() {
    setSelectedOrder(null)
    const gridApi = (orderLogGridRef as MutableRefObject<GridApi<InvoiceTransaction>>)?.current
    if (gridApi) {
      gridApi.deselectAll()
    }
    
  }

  return (
    <Vertical flex={1.2} style={{overflow: 'hidden'}}>
      {selectedInvoice?.transactions?.length === 0 ? (
        <Card
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Vertical horizontalCenter verticalCenter>
            <MaterialIcon icon='info' style={{ fontSize: '48px', color: 'var(--theme-color-1)' }} />
            <Texto category='h4' style={{ marginTop: '16px' }}>
              No Orders Found
            </Texto>
            <Texto
              appearance='light'
              style={{
                textAlign: 'center',
                maxWidth: '300px',
                marginTop: '8px',
              }}
            >
              There are no orders associated with this invoice group yet.
            </Texto>
          </Vertical>
        </Card>
      ) : (
      <GraviGrid<InvoiceTransaction, IFilter>
        primaryKey='order_id'
        externalRef={orderLogGridRef as MutableRefObject<GridApi<InvoiceTransaction>>}
        rowData={selectedInvoice?.transactions || []}
        showColumnsToolbar={false}
        storageKey='invoice-order-log'
        fullWidthSearchBar
        controlBarProps={{
          title: 'Order Logs',
          hideActiveFilters: true,
          actionButtons: 
            <GraviButton
              icon={<CloseOutlined />}
              onClick={handleRowDeselection}
              buttonText='Deselect'
              disabled={!selectedOrder}
            />
        }}
        onSelectionChanged={({ api }) => {
          const selectedRows = api.getSelectedRows()
          const newSelectedOrder = selectedRows[0] || null
          if (selectedOrder?.order_number === newSelectedOrder) {
            setSelectedOrder(null)
          } else {
            setSelectedOrder(newSelectedOrder)
          }
        }}
        onGridConfigChanged={(config: GridConfigState) => updateSetting('gridConfig:invoice-order-log', config)}
        agPropOverrides={{
          columnDefs: orderLogColumnDefs,
          rowGroupPanelShow: 'never',
          rowSelection: 'single',
          suppressRowClickSelection: false,
          getRowId: (params) =>  params.data?.order_id || String(Math.random()),
          context: {
            rebillUpdateActions: isRebillMode ? rebillUpdateActions : undefined,
            triggerDrawer,
            counterpartyId: invoiceGroupDetails?.counterparty_id,
            bookType: invoiceGroupDetails?.book_type,
          },
          scrollbarWidth: 0,
          getRowStyle: (params: RowClassParams<InvoiceTransaction>): RowStyle | undefined => {
            if (!params.data) return undefined
            if (params.data?.credit_rebill_metadata?.impact_type) {
              return { backgroundColor: 'var(--theme-warning-dim)' }
            }
            return undefined
          },
        }}
      />
      )}
    </Vertical>
  )
}

const getOrderLogColumnDefs:(selectedInvoice: Invoice | null) => ColDef<InvoiceTransaction>[] = (selectedInvoice) => [
  {
    headerName: 'DELIVERED',
    field: 'order_number',
    width: 150,
    filter: true,
    sortable: true,
    minWidth: 120,
    cellRenderer: (params: { data: InvoiceTransaction }) => {
      return (
        <Vertical verticalCenter style={{ margin: '-6px 0px' }}>
          <Texto category='label' appearance='hint' style={{ lineHeight: '1em' }}>
            {formatters.DATE(params.data?.date)}
          </Texto>
          <Texto category='p2' appearance='primary' style={{ cursor: 'pointer' }}>
            {params.data?.order_number}
          </Texto>
        </Vertical>
      )
    },
    valueFormatter: (params) => dayjs(params.value).format('MM/DD/YY HH:mm'),
    cellStyle: { display: 'flex', alignItems: 'flex-start' },
    headerClass: 'ag-grid-header-cell',
  },

  {
    headerName: 'DETAIL',
    field: 'distance',
  filter: true,
  sortable: true,
  minWidth: 120,
  width: 120,
  cellRenderer: (params: { data: InvoiceTransaction }) => (
    <Vertical verticalCenter style={{ margin: '-6px 0px' }}>
      <Texto style={{ lineHeight: '1em' }}>{addCommasToNumber(params.data?.distance)} MI</Texto>
      <Texto category='label' appearance='light'>
        {addCommasToNumber(params.data?.volume)} GAL
      </Texto>
    </Vertical>
  ),
  cellStyle: { display: 'flex', alignItems: 'flex-start' },
  headerClass: 'ag-grid-header-cell',
  },
  {
    headerName: '',
    width: 90,
    minWidth: 90,
    pinned: 'right',
  cellStyle: { display: 'flex', alignItems: 'flex-start' },
  cellRenderer: (params: ICellRendererParams<InvoiceTransaction>) => {
    // Check if we're in credit rebill mode based on the invoice type
    const isCreditRebillMode = selectedInvoice?.type === 'rebill' || selectedInvoice?.type === 'credit'
    if (!params.data) return null
    return (
      <Horizontal gap={10}>
        <AddFreightLineItemPopover
          actionType='create'
          order={params.data}
          gridApi={params.api}
          context={params.context}
          isCreditRebillMode={isCreditRebillMode}
        >
          <GraviButton size='small' icon={<FileAddOutlined />} />
        </AddFreightLineItemPopover>
        <GraviButton
          className='pt-1'
          icon={<MaterialIcon icon='top_panel_close' />}
          size='small'
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            params.context?.triggerDrawer(params.data?.order_id)
          }}
        />
      </Horizontal>
    )
  },
  },
]

